import { isMembershipFirstSite } from "@/lib/membership-first"
import type { Locale } from "./insurance-types"

/**
 * The coverage checker on the membership-first site (`isMembershipFirstSite`).
 * That site names the three MRI packages after the full-body MRI: Core is the
 * Full-body MRI, then Full-body MRI Advanced and Full-body MRI Pro (12.10).
 * The checker keeps its service ids and prices; only what a visitor reads
 * changes.
 */

/** The checker's copy on that site, laid over `data/insurance/i18n`. */
const CHECKER_STRINGS: Record<Locale, Record<string, string>> = {
  en: {
    "form.service.name.core-mri": "Full-body MRI",
    "form.service.name.advanced-mri": "Full-body MRI Advanced",
    "form.service.name.pro-mri": "Full-body MRI Pro",
    "form.service.desc.advanced-mri":
      "Whole-body scan with prostate or pelvic imaging, body composition and an advanced blood test.",
    "form.service.desc.pro-mri":
      "Everything in Full-body MRI Advanced, plus brain, hip and knee screening and hormone and B-vitamin tests.",
  },
  de: {
    "form.service.name.core-mri": "Ganzkörper-MRI",
    "form.service.name.advanced-mri": "Ganzkörper-MRI Advanced",
    "form.service.name.pro-mri": "Ganzkörper-MRI Pro",
    "form.service.desc.advanced-mri":
      "Ganzkörper-Scan mit Bildgebung von Prostata oder Becken, Körperzusammensetzung und einem erweiterten Bluttest.",
    "form.service.desc.pro-mri":
      "Alles aus dem Ganzkörper-MRI Advanced, plus Gehirn-, Hüft- und Knie-Screening sowie Hormon- und B-Vitamin-Tests.",
  },
  fr: {
    "form.service.name.core-mri": "IRM corps entier",
    "form.service.name.advanced-mri": "IRM corps entier Advanced",
    "form.service.name.pro-mri": "IRM corps entier Pro",
    "form.service.desc.advanced-mri":
      "Examen du corps entier avec imagerie de la prostate ou du bassin, composition corporelle et bilan sanguin avancé.",
    "form.service.desc.pro-mri":
      "Tout ce que comprend l'IRM corps entier Advanced, plus le dépistage du cerveau, des hanches et des genoux, et les tests hormones et vitamines B.",
  },
}

/** The checker's strings for `locale` on the site, or none off it. */
export function membershipFirstCheckerStrings(
  locale: Locale,
  country: string
): Record<string, string> | null {
  return isMembershipFirstSite(country) ? CHECKER_STRINGS[locale] : null
}

/**
 * A service's label with its list price, "Full-body MRI (CHF 2'490)", the
 * shape the CMS names carry, for a service the site renames. Null for the
 * rest, which keep the CMS name.
 */
export function membershipFirstServiceLabel(
  serviceId: string,
  price: number,
  locale: Locale,
  country: string
): string | null {
  const name = membershipFirstCheckerStrings(locale, country)?.[
    `form.service.name.${serviceId}`
  ]
  if (!name) return null
  const chf = String(price).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
  return `${name} (CHF ${chf})`
}
